import React, { useMemo, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DollarSign, Heart, Sparkles, Users, Mountain, Plus, Check, X, CircleDot } from "lucide-react";
import { toast } from "sonner";

const PILLAR_META = {
  financial: { icon: DollarSign, color: "#3e6b52", bg: "#e9f0ec", label: "Financial" },
  health: { icon: Heart, color: "#b94e4e", bg: "#f5e6e6", label: "Health" },
  spirituality: { icon: Sparkles, color: "#7a5fb0", bg: "#ede8f5", label: "Spirituality" },
  relationships: { icon: Users, color: "#c18848", bg: "#f5ece0", label: "Relationships" },
  endgame: { icon: Mountain, color: "#486a8f", bg: "#e7edf5", label: "Endgame" },
};

export const HORIZONS = [
  { key: "today", label: "Today", days: 1 },
  { key: "week", label: "8 Days", days: 8 },
  { key: "month", label: "30 Days", days: 30 },
  { key: "quarter", label: "90 Days", days: 90 },
  { key: "year", label: "1 Year", days: 365 },
  { key: "five_year", label: "5 Years", days: 365 * 5 },
  { key: "lifetime", label: "Lifetime", days: 365 * 25 },
];

function horizonFromDays(d) {
  if (d <= 1) return "today";
  if (d <= 8) return "week";
  if (d <= 30) return "month";
  if (d <= 90) return "quarter";
  if (d <= 365) return "year";
  if (d <= 365 * 5) return "five_year";
  return "lifetime";
}

export default function RoadmapGantt({ roadmap, onChange }) {
  const [horizonKey, setHorizonKey] = useState("week");
  const [adding, setAdding] = useState(false);
  const [newM, setNewM] = useState({ title: "", pillar: "financial", days_from_now: 30 });

  const horizon = HORIZONS.find((h) => h.key === horizonKey) || HORIZONS[1];
  const milestones = roadmap?.milestones || [];
  const current = roadmap?.horizons?.[horizonKey];

  // Only show milestones that fall within this horizon's window
  const visibleMs = useMemo(() => {
    return milestones
      .filter((m) => (m.days_from_now ?? 0) <= horizon.days)
      .sort((a, b) => (a.days_from_now || 0) - (b.days_from_now || 0));
  }, [milestones, horizon.days]);

  const addMilestone = async () => {
    if (!newM.title.trim()) return;
    try {
      const r = await axios.post(
        `${API}/roadmap/milestones`,
        { ...newM, horizon: horizonFromDays(Number(newM.days_from_now)) }
      );
      toast.success("Milestone added.");
      setNewM({ title: "", pillar: "financial", days_from_now: 30 });
      setAdding(false);
      onChange && onChange({
        ...roadmap,
        milestones: [...milestones, r.data],
      });
    } catch (e) {
      toast.error("Couldn't add milestone.");
    }
  };

  const completeMilestone = async (id) => {
    try {
      await axios.post(`${API}/roadmap/milestones/${id}/complete`, {});
      const updated = milestones.map((m) => (m.id === id ? { ...m, status: "completed" } : m));
      onChange && onChange({ ...roadmap, milestones: updated });
    } catch (e) {
      toast.error("Couldn't update.");
    }
  };

  const deleteMilestone = async (id) => {
    try {
      await axios.delete(`${API}/roadmap/milestones/${id}`);
      onChange && onChange({ ...roadmap, milestones: milestones.filter((m) => m.id !== id) });
    } catch (e) {
      toast.error("Couldn't delete.");
    }
  };

  return (
    <div>
      {/* Horizon picker */}
      <div className="mb-6 -mx-1 overflow-x-auto">
        <div className="flex gap-2 px-1 min-w-max">
          {HORIZONS.map((h) => {
            const active = h.key === horizonKey;
            return (
              <button
                key={h.key}
                onClick={() => setHorizonKey(h.key)}
                className={`px-4 py-2 rounded-full font-mono-eyebrow text-[11px] whitespace-nowrap transition-all ${
                  active
                    ? "bg-[#486a8f] text-white shadow-[0_6px_20px_-10px_rgba(72,106,143,0.6)]"
                    : "bg-white text-[#486a8f] border border-[#e6dcc6] hover:bg-[#faf6ec]"
                }`}
              >
                {h.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Focus for current horizon */}
      {current?.focus && (
        <div className="mb-6 rounded-xl bg-[#e7edf5] border border-[#cdd8e8] p-5">
          <p className="font-mono-eyebrow text-[#486a8f] mb-1">
            {horizon.label} focus
          </p>
          <p className="font-display text-[20px] md:text-[22px] text-[#0f1724] leading-tight">
            {current.focus}
          </p>
          {current?.tasks?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {current.tasks.slice(0, 5).map((t, i) => (
                <li key={i} className="font-body text-[14px] text-[#2b3442] flex gap-2">
                  <CircleDot size={13} className="text-[#486a8f] mt-1 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Gantt chart */}
      <Gantt
        milestones={visibleMs}
        horizonDays={horizon.days}
        onComplete={completeMilestone}
        onDelete={deleteMilestone}
      />

      {/* Add milestone */}
      <div className="mt-6">
        {!adding ? (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-dashed border-[#486a8f] text-[#486a8f] font-body text-[13px] hover:bg-[#e7edf5] transition-colors"
          >
            <Plus size={14} /> Add milestone
          </button>
        ) : (
          <div className="rounded-xl bg-white border border-[#eadfc9] p-4 space-y-3">
            <Input
              value={newM.title}
              onChange={(e) => setNewM((n) => ({ ...n, title: e.target.value }))}
              placeholder="Milestone title (e.g., Launch first course)"
              className="h-10 text-[14px]"
            />
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
              <select
                value={newM.pillar}
                onChange={(e) => setNewM((n) => ({ ...n, pillar: e.target.value }))}
                className="h-10 px-3 rounded-md border border-[#e6dcc6] bg-white text-[13px] text-[#2b3442]"
              >
                {Object.entries(PILLAR_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <Input
                type="number"
                min="0"
                value={newM.days_from_now}
                onChange={(e) => setNewM((n) => ({ ...n, days_from_now: e.target.value }))}
                className="h-10 w-24 text-[13px]"
                placeholder="Days"
              />
              <span className="font-body text-[12px] text-[#6b7280]">days out</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAdding(false)} className="rounded-full text-[#6b7280]">
                Cancel
              </Button>
              <Button onClick={addMilestone} className="rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white">
                Add
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Gantt({ milestones, horizonDays, onComplete, onDelete }) {
  const pillars = Object.keys(PILLAR_META);
  const [hovered, setHovered] = useState(null);

  // Ticks along the axis
  const ticks = useMemo(() => {
    if (horizonDays <= 8) return [0, 1, 2, 4, 6, 8].map((d) => ({ d, l: d === 0 ? "Now" : `${d}d` }));
    if (horizonDays <= 30) return [0, 7, 14, 21, 30].map((d) => ({ d, l: d === 0 ? "Now" : `${d}d` }));
    if (horizonDays <= 90) return [0, 15, 30, 60, 90].map((d) => ({ d, l: d === 0 ? "Now" : `${d}d` }));
    if (horizonDays <= 365) return [0, 30, 90, 180, 365].map((d) => ({ d, l: d === 0 ? "Now" : d >= 30 ? `${Math.round(d/30)}mo` : `${d}d` }));
    if (horizonDays <= 365 * 5)
      return [0, 365, 365 * 2, 365 * 3, 365 * 5].map((d) => ({ d, l: d === 0 ? "Now" : `${Math.round(d / 365)}y` }));
    return [0, 365, 365 * 5, 365 * 10, 365 * 25].map((d) => ({ d, l: d === 0 ? "Now" : `${Math.round(d / 365)}y` }));
  }, [horizonDays]);

  return (
    <div className="rounded-2xl bg-white border border-[#eadfc9] p-5 overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Axis labels */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-[110px] shrink-0" />
          <div className="relative flex-1 h-6">
            {ticks.map((t, i) => (
              <div
                key={i}
                className="absolute top-0 -translate-x-1/2 font-mono-eyebrow text-[10px] text-[#6b7280]"
                style={{ left: `${Math.min(100, (t.d / horizonDays) * 100)}%` }}
              >
                {t.l}
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div className="space-y-2.5">
          {pillars.map((pKey) => {
            const meta = PILLAR_META[pKey];
            const Icon = meta.icon;
            const pillarMs = milestones.filter((m) => (m.pillar || "").toLowerCase() === pKey);
            return (
              <div key={pKey} className="flex items-center gap-3">
                <div
                  className="flex items-center gap-2 w-[110px] shrink-0 px-2.5 py-1.5 rounded-lg"
                  style={{ background: meta.bg }}
                >
                  <Icon size={13} style={{ color: meta.color }} />
                  <span className="font-body text-[12px] font-medium" style={{ color: meta.color }}>
                    {meta.label}
                  </span>
                </div>
                <div className="relative flex-1 h-10 rounded-lg bg-[#faf6ec] border border-[#f2ecdc]">
                  {/* Tick lines */}
                  {ticks.map((t, i) => (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0 w-px bg-[#eadfc9]"
                      style={{ left: `${Math.min(100, (t.d / horizonDays) * 100)}%` }}
                    />
                  ))}
                  {/* Milestone dots */}
                  {pillarMs.map((m) => {
                    const leftPct = Math.max(0.5, Math.min(99.5, (m.days_from_now / horizonDays) * 100));
                    const done = m.status === "completed";
                    return (
                      <div
                        key={m.id}
                        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${leftPct}%` }}
                        onMouseEnter={() => setHovered(m.id)}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <button
                          onClick={() => onComplete(m.id)}
                          title={m.title}
                          className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-125 ${
                            done ? "bg-[#3e6b52] border-[#3e6b52]" : "border-2 border-white shadow"
                          }`}
                          style={!done ? { background: meta.color } : {}}
                        >
                          {done && <Check size={11} className="text-white mx-auto" strokeWidth={3} />}
                        </button>
                        {hovered === m.id && (
                          <div className="absolute z-20 bottom-full mb-2 left-1/2 -translate-x-1/2 w-60 rounded-lg bg-[#0f1724] text-white p-3 shadow-xl">
                            <p className="font-body text-[12px] font-medium leading-snug mb-1">{m.title}</p>
                            {m.description && (
                              <p className="font-body text-[11px] text-white/70 leading-snug mb-2">{m.description}</p>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="font-mono-eyebrow text-[9px] text-white/60">
                                {m.target_date || `${m.days_from_now}d`}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(m.id);
                                }}
                                className="text-white/60 hover:text-[#b94e4e]"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Milestone list below chart */}
        {milestones.length > 0 && (
          <div className="mt-6 pt-5 border-t border-[#eadfc9]">
            <p className="font-mono-eyebrow text-[#486a8f] mb-3">Milestones in this horizon</p>
            <div className="space-y-2">
              {milestones.map((m) => {
                const meta = PILLAR_META[(m.pillar || "endgame").toLowerCase()] || PILLAR_META.endgame;
                const Icon = meta.icon;
                const done = m.status === "completed";
                return (
                  <div
                    key={m.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border ${
                      done ? "bg-[#e9f0ec]/60 border-[#cddfd3]" : "bg-white border-[#eadfc9]"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                      <Icon size={13} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-body text-[13px] font-medium text-[#0f1724] ${done ? "line-through opacity-60" : ""}`}>
                        {m.title}
                      </p>
                      <p className="font-body text-[11px] text-[#6b7280]">
                        {m.target_date || `In ${m.days_from_now}d`}
                        {m.measurable ? ` · ${m.measurable}` : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => onComplete(m.id)}
                      disabled={done}
                      title="Mark complete"
                      className={`w-8 h-8 rounded-full flex items-center justify-center border transition-colors ${
                        done
                          ? "bg-[#3e6b52] border-[#3e6b52] text-white"
                          : "bg-white border-[#eadfc9] text-[#6b7280] hover:bg-[#f0e9d6]"
                      }`}
                    >
                      <Check size={13} strokeWidth={3} />
                    </button>
                    <button
                      onClick={() => onDelete(m.id)}
                      title="Remove"
                      className="w-8 h-8 rounded-full flex items-center justify-center bg-white border border-[#eadfc9] text-[#6b7280] hover:text-[#b94e4e] hover:border-[#b94e4e]"
                    >
                      <X size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
